import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  bankAccountApi,
  senderProfileApi,
  userPreferencesApi,
} from '../services/settingsApi'
import {
  BankAccount,
  BankAccountCreate,
  BankAccountUpdate,
  SenderProfile,
  SenderProfileCreate,
  SenderProfileUpdate,
  UserPreferences,
  UserPreferencesUpdate,
} from '../types/settings'

// Query keys
export const settingsKeys = {
  all: ['settings'] as const,
  bankAccounts: () => [...settingsKeys.all, 'bank-accounts'] as const,
  bankAccount: (id: number) => [...settingsKeys.bankAccounts(), id] as const,
  senderProfiles: () => [...settingsKeys.all, 'sender-profiles'] as const,
  senderProfile: (id: number) => [...settingsKeys.senderProfiles(), id] as const,
  preferences: () => [...settingsKeys.all, 'preferences'] as const,
}

// Bank Account Hooks
export function useBankAccounts() {
  return useQuery({
    queryKey: settingsKeys.bankAccounts(),
    queryFn: () => bankAccountApi.getAll(),
  })
}

export function useBankAccount(id: number) {
  return useQuery({
    queryKey: settingsKeys.bankAccount(id),
    queryFn: () => bankAccountApi.getById(id),
    enabled: !!id,
  })
}

export function useCreateBankAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: BankAccountCreate) => bankAccountApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.bankAccounts() })
    },
  })
}

export function useUpdateBankAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: BankAccountUpdate }) =>
      bankAccountApi.update(id, data),
    onSuccess: (updatedAccount: BankAccount) => {
      queryClient.setQueryData(
        settingsKeys.bankAccount(updatedAccount.id),
        updatedAccount
      )
      queryClient.invalidateQueries({ queryKey: settingsKeys.bankAccounts() })
    },
  })
}

export function useDeleteBankAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => bankAccountApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: settingsKeys.bankAccount(id) })
      queryClient.invalidateQueries({ queryKey: settingsKeys.bankAccounts() })
    },
  })
}

export function useSetDefaultBankAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => bankAccountApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.bankAccounts() })
    },
  })
}

// Sender Profile Hooks
export function useSenderProfiles() {
  return useQuery({
    queryKey: settingsKeys.senderProfiles(),
    queryFn: () => senderProfileApi.getAll(),
  })
}

export function useSenderProfile(id: number) {
  return useQuery({
    queryKey: settingsKeys.senderProfile(id),
    queryFn: () => senderProfileApi.getById(id),
    enabled: !!id,
  })
}

export function useCreateSenderProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SenderProfileCreate) => senderProfileApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.senderProfiles() })
    },
  })
}

export function useUpdateSenderProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SenderProfileUpdate }) =>
      senderProfileApi.update(id, data),
    onSuccess: (updatedProfile: SenderProfile) => {
      queryClient.setQueryData(
        settingsKeys.senderProfile(updatedProfile.id),
        updatedProfile
      )
      queryClient.invalidateQueries({ queryKey: settingsKeys.senderProfiles() })
    },
  })
}

export function useDeleteSenderProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => senderProfileApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: settingsKeys.senderProfile(id) })
      queryClient.invalidateQueries({ queryKey: settingsKeys.senderProfiles() })
    },
  })
}

export function useSetDefaultSenderProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => senderProfileApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.senderProfiles() })
    },
  })
}

// User Preferences Hooks
export function useUserPreferences() {
  return useQuery({
    queryKey: settingsKeys.preferences(),
    queryFn: () => userPreferencesApi.get(),
  })
}

export function useUpdateUserPreferences() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UserPreferencesUpdate) => userPreferencesApi.update(data),
    onSuccess: (updatedPreferences: UserPreferences) => {
      queryClient.setQueryData(settingsKeys.preferences(), updatedPreferences)
    },
  })
}
